// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import axios from 'axios';
import { Checkbox, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import type { BreakthroughCashPositionSectionContent, ProposalSection } from '../../../../../types/proposal';
import { formatCurrency } from '../../../proposalDisplay';

export type SectionCashPositionProps = Readonly<{
  proposalId: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the accounts
   * table + total are already read-only and render unchanged; the advisor's
   * internal "no accounts to report" acknowledgement checkbox is hidden.
   * Defaults to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): BreakthroughCashPositionSectionContent {
  return (section.content as BreakthroughCashPositionSectionContent | null) ?? {};
}

/**
 * BR_CASH_POSITION (PRD §4 row 5) — server-owned account balances projected
 * from Financial Position; the advisor cannot edit the figures. When the
 * client has no cash accounts on file, an explicit acknowledgement gates the
 * section as complete (mirrors the backend `CashPositionSectionResolver`'s
 * `noAccountsAcknowledged` rule).
 */
export function SectionCashPosition({
  proposalId,
  section,
  onSaved,
  readOnly = false,
}: SectionCashPositionProps) {
  const { showToast } = useToast();
  const content = readContent(section);
  const accounts = content.accounts ?? [];
  const [acknowledged, setAcknowledged] = useState(Boolean(content.noAccountsAcknowledged));
  const [saving, setSaving] = useState(false);

  const handleAcknowledge = useCallback(
    async (checked: boolean) => {
      if (saving) return;
      setAcknowledged(checked);
      setSaving(true);
      try {
        const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
          content: { ...content, noAccountsAcknowledged: checked },
        });
        onSaved(updated);
      } catch (err) {
        setAcknowledged(!checked);
        showToast(apiErrorMessage(err, 'Failed to save Cash Position'), 'error');
      } finally {
        setSaving(false);
      }
    },
    [saving, content, proposalId, section.sectionKey, onSaved, showToast],
  );

  if (accounts.length === 0) {
    return (
      <div className="pension-builder__section" data-testid="section-br-cash-position">
        <p className="pension-builder__empty" data-testid="section-br-cash-position-empty">
          No cash accounts found in this relationship's Financial Position yet.
        </p>
        {!readOnly && (
          <Checkbox
            label="This client genuinely has no cash accounts to report"
            checked={acknowledged}
            onChange={(e) => void handleAcknowledge(e.target.checked)}
            disabled={saving}
            data-testid="section-br-cash-position-acknowledge"
          />
        )}
      </div>
    );
  }

  return (
    <div className="pension-builder__section" data-testid="section-br-cash-position">
      <table className="pension-builder__table" data-testid="section-br-cash-position-table">
        <thead>
          <tr>
            <th>Owner</th>
            <th>Institution</th>
            <th>Type</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account, index) => (
            <tr key={account.accountId ?? index}>
              <td>{account.owner ?? '—'}</td>
              <td>{account.institution || '—'}</td>
              <td>{account.type ?? account.label ?? '—'}</td>
              <td>{formatCurrency(account.balance, account.currency ?? content.currencyCode) ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pension-builder__facts-card-title" data-testid="section-br-cash-position-total">
        Total cash: {formatCurrency(content.totalCash, content.currencyCode) ?? '—'}
      </p>
    </div>
  );
}

export default SectionCashPosition;
